import React from 'react';
import { FiPlus, FiX, FiFilter, FiBookOpen, FiFileText } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { SessionListItem } from '../../../../services/sessionService';
import { ChatSession } from '../types';
import voiceImageConfig from '../../../../config/deepgram_aura_v1_voice_images.json';

type ExamSidebarMode = 'topics' | 'notes';

interface ExamSidebarTopic {
  topicId: string;
  title: string;
  order: number;
  subtopics?: string[];
}

interface ExamNotesTreeNode {
  nodeId: string;
  label: string;
  depth: number;
  type: 'topic' | 'subtopic';
  parentNodeId?: string;
}

interface ChatSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  onNewChat: () => void;
  isExamMode: boolean;
  isDateFilterOpen: boolean;
  onToggleDateFilter: () => void;
  startDate: string;
  endDate: string;
  onStartDateChange: (value: string) => void;
  onEndDateChange: (value: string) => void;
  onApplyDateFilter: () => void;
  onClearDateFilter: () => void;
  isLoadingSessions: boolean;
  sessionsError: string | null;
  onRetrySessions: () => void;
  latestSessions: SessionListItem[];
  selectedSessionId: string | null;
  onSelectSession: (sessionId: string) => void;
  currentPage: number;
  totalPages: number;
  totalSessions: number;
  hasPrevPage: boolean;
  hasNextPage: boolean;
  onPrevPage: () => void;
  onNextPage: () => void;
  onPageChange: (page: number) => void;
  groupedSessions: Record<string, ChatSession[]>;
  onSubjectDebugSelect: (subject: string) => void;
  selectedSubject: string;
  selectedDocumentId: string;
  selectedExamName: string;
  sessionId: string | null;
  isZapTalkMode?: boolean;
  examSidebarMode: ExamSidebarMode;
  onExamSidebarModeChange: (mode: ExamSidebarMode) => void;
  examTopics: ExamSidebarTopic[];
  examNotesTreeNodes: ExamNotesTreeNode[];
  selectedExamTopicNodeId: string;
  lockedExamNodeIds: Record<string, boolean>;
  selectedNotesTopicId: string;
  noteCountsByTopic: Record<string, number>;
  isLoadingExamTopics: boolean;
  examTopicsError: string | null;
  onRetryExamTopics: () => void;
  onSelectExamTopic: (topicId: string) => void;
  onSelectNotesTopic: (topicId: string) => void;
  selectedVoice: string;
  isBotSpeaking: boolean;
}

type VoiceImageEntry = {
  model: string;
  image: string;
};

// Teacher photos are bundled from src/assets/images/teachers (hashed by webpack).
// require.context keeps the build green even if a file is missing. Files are
// matched by base name, so "man-1.jpg", "man-1.jpeg" and "man-1.png" all
// satisfy a config entry of "man-1.jpeg".
const teacherImageContext = (require as any).context('../../../../assets/images/teachers', false, /\.(png|jpe?g|webp)$/i);
const teacherImageName = (file: string) => file.replace(/^\.\//, '').replace(/\.[a-z0-9]+$/i, '').toLowerCase();
const TEACHER_IMAGES: Record<string, string> = teacherImageContext.keys().reduce(
  (acc: Record<string, string>, key: string) => {
    acc[teacherImageName(key)] = teacherImageContext(key);
    return acc;
  },
  {} as Record<string, string>,
);

const DEFAULT_TEACHER_IMAGE = TEACHER_IMAGES['female-1'] || '';

const VOICE_IMAGE_MAP: Record<string, string> = (voiceImageConfig.deepgram_aura_v1_voice_images as VoiceImageEntry[]).reduce(
  (acc, voice) => {
    const src = voice.model && voice.image ? TEACHER_IMAGES[teacherImageName(voice.image)] : undefined;
    if (src) {
      acc[voice.model] = src;
    }
    return acc;
  },
  {} as Record<string, string>,
);

const ChatSidebar: React.FC<ChatSidebarProps> = ({
  isOpen,
  onClose,
  onNewChat,
  isExamMode,
  isDateFilterOpen,
  onToggleDateFilter,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  onApplyDateFilter,
  onClearDateFilter,
  isLoadingSessions,
  sessionsError,
  onRetrySessions,
  latestSessions,
  selectedSessionId,
  onSelectSession,
  currentPage,
  totalPages,
  totalSessions,
  hasPrevPage,
  hasNextPage,
  onPrevPage,
  onNextPage,
  onPageChange,
  groupedSessions,
  onSubjectDebugSelect,
  selectedSubject,
  selectedDocumentId,
  selectedExamName,
  sessionId,
  isZapTalkMode = false,
  examSidebarMode,
  onExamSidebarModeChange,
  examTopics,
  examNotesTreeNodes,
  selectedExamTopicNodeId,
  lockedExamNodeIds,
  selectedNotesTopicId,
  noteCountsByTopic,
  isLoadingExamTopics,
  examTopicsError,
  onRetryExamTopics,
  onSelectExamTopic,
  onSelectNotesTopic,
  selectedVoice,
  isBotSpeaking,
}) => {
  const navigate = useNavigate();
  const [isTeacherGifLoaded, setIsTeacherGifLoaded] = React.useState(false);
  const [isTeacherGifError, setIsTeacherGifError] = React.useState(false);
  const teacherImageSrc = VOICE_IMAGE_MAP[selectedVoice] || DEFAULT_TEACHER_IMAGE;

  React.useEffect(() => {
    setIsTeacherGifLoaded(false);
    // No bundled file for this teacher -> show the "unavailable" state instead of a spinner.
    setIsTeacherGifError(!teacherImageSrc);
  }, [teacherImageSrc]);

  const getSessionDisplayTitle = (session: SessionListItem) => {
    if (!isZapTalkMode) {
      return session.subject;
    }

    const topic = typeof session.topic === 'string' ? session.topic.trim() : '';
    return topic || 'Mental Health';
  };

  return (
    <aside className={`sidebar ${isOpen ? 'open' : ''} `}>
      <div className="sidebar-header">
        <span className="logo-text" onClick={() => navigate('/dashboard')} style={{ cursor: 'pointer' }}>
          ZapTick AI
        </span>
        <button className="sidebar-close" onClick={onClose} aria-label="Close sidebar">
          <FiX size={20} />
        </button>
      </div>

      {!isZapTalkMode && (
        <div className="teacher-gif-card">
          {!isTeacherGifLoaded && !isTeacherGifError && (
            <div className="teacher-gif-placeholder" role="status" aria-label="Loading teacher preview">
              <span className="teacher-gif-loader" aria-hidden="true" />
            </div>
          )}
          {isTeacherGifError && (
            <div className="teacher-gif-placeholder">Teacher preview unavailable</div>
          )}
          {!isTeacherGifError && (
            <img
              className={`teacher-gif-image ${isBotSpeaking ? 'speaking' : ''}`.trim()}
              src={teacherImageSrc}
              alt="Virtual teacher"
              loading="eager"
              decoding="async"
              referrerPolicy="no-referrer"
              onLoad={() => setIsTeacherGifLoaded(true)}
              onError={() => {
                setIsTeacherGifLoaded(false);
                setIsTeacherGifError(true);
              }}
            />
          )}
        </div>
      )}

      {!isExamMode && !isZapTalkMode && (
        <button className="new-chat-btn" onClick={onNewChat}>
          <FiPlus size={16} />
          New chat
        </button>
      )}

      <nav className="sidebar-sections">
        {!isExamMode && (
          <>
            <div className="latest-sessions">
              <div className="sessions-header">
                <h3 className="period-title">{isZapTalkMode ? 'Counseling sessions' : 'Notes'}</h3>
                {!isZapTalkMode && (
                  <button className="filter-btn" onClick={onToggleDateFilter} title="Filter by date range">
                    <FiFilter size={16} />
                  </button>
                )}
              </div>

              {!isZapTalkMode && isDateFilterOpen && (
                <div className="date-filter-container">
                  <div className="date-inputs">
                    <div className="date-input-group">
                      <label htmlFor="start-date">From:</label>
                      <input
                        id="start-date"
                        type="date"
                        value={startDate}
                        onChange={(e) => onStartDateChange(e.target.value)}
                        className="date-input"
                      />
                    </div>
                    <div className="date-input-group">
                      <label htmlFor="end-date">To:</label>
                      <input
                        id="end-date"
                        type="date"
                        value={endDate}
                        onChange={(e) => onEndDateChange(e.target.value)}
                        className="date-input"
                      />
                    </div>
                  </div>
                  <div className="date-filter-actions">
                    <button className="apply-filter-btn" onClick={onApplyDateFilter} disabled={!startDate || !endDate}>
                      Apply Filter
                    </button>
                    <button className="clear-filter-btn" onClick={onClearDateFilter}>
                      Clear
                    </button>
                  </div>
                </div>
              )}

              {isLoadingSessions ? (
                <div className="loading-sessions">
                  <span className="loading-text">Loading sessions...</span>
                </div>
              ) : sessionsError ? (
                <div className="sessions-error">
                  <span className="error-text">{sessionsError}</span>
                  <button className="retry-btn" onClick={onRetrySessions} title="Retry loading sessions">
                    Retry
                  </button>
                </div>
              ) : latestSessions && latestSessions.length > 0 ? (
                <>
                  <div className="sessions-list">
                    {latestSessions.map((session) => (
                      <button
                        key={session._id}
                        className={`chat-session-btn ${selectedSessionId === session._id ? 'active' : ''}`}
                        onClick={() => onSelectSession(session._id)}
                        title={`Session: ${getSessionDisplayTitle(session)} - ${session.formattedDateTime || session.createdAt}`}
                      >
                        <div className="session-info">
                          <span className="session-subject">{getSessionDisplayTitle(session)}</span>
                          <span className="session-date">
                            {session.formattedDateTime ||
                              new Date(session.createdAt).toLocaleString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>

                  {totalPages > 1 && (
                    <div className="pagination-controls">
                      <div className="pagination-info">
                        <span className="pagination-text">
                          Page {currentPage} of {totalPages} ({totalSessions} total)
                        </span>
                      </div>
                      <div className="pagination-buttons">
                        <button className="pagination-btn" onClick={onPrevPage} disabled={!hasPrevPage} title="Previous page">
                          {'<-'}
                        </button>

                        <div className="page-numbers">
                          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                            let pageNum: number;
                            if (totalPages <= 5) {
                              pageNum = i + 1;
                            } else if (currentPage <= 3) {
                              pageNum = i + 1;
                            } else if (currentPage >= totalPages - 2) {
                              pageNum = totalPages - 4 + i;
                            } else {
                              pageNum = currentPage - 2 + i;
                            }

                            return (
                              <button
                                key={pageNum}
                                className={`page-btn ${currentPage === pageNum ? 'active' : ''}`}
                                onClick={() => onPageChange(pageNum)}
                                title={`Page ${pageNum}`}
                              >
                                {pageNum}
                              </button>
                            );
                          })}
                        </div>

                        <button className="pagination-btn" onClick={onNextPage} disabled={!hasNextPage} title="Next page">
                          {'->'}
                        </button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="no-sessions">
                  <span className="no-sessions-text">No recent sessions</span>
                </div>
              )}
            </div>

            <div className="chat-history">
              {Object.entries(groupedSessions).map(
                ([period, sessions]) =>
                  sessions.length > 0 && (
                    <div key={period} className="history-group">
                      <h3 className="period-title">{period}</h3>
                      {sessions.map((session) => (
                        <button
                          key={session._id}
                          className="chat-session-btn"
                          onClick={() => {
                            if (window.innerWidth <= 768) {
                              onClose();
                            }
                          }}
                        >
                          {session.subject}
                        </button>
                      ))}
                    </div>
                  ),
              )}
            </div>
          </>
        )}

        {isExamMode && (
          <div className="exam-sidebar">
            <div className="exam-mode-status-card">
              <div className="exam-mode-status-icon">
                <FiBookOpen size={16} />
              </div>
              <h3>Exam Mode Active</h3>
              <p>You're currently in exam mode. Focus on your study materials.</p>
            </div>

            <div className="exam-sidebar-tabs" role="tablist" aria-label="Exam navigation tabs">
              <button
                type="button"
                className={`exam-sidebar-tab ${examSidebarMode === 'topics' ? 'active' : ''}`}
                onClick={() => onExamSidebarModeChange('topics')}
                role="tab"
                aria-selected={examSidebarMode === 'topics'}
              >
                <FiBookOpen size={14} />
                Topics
              </button>
              <button
                type="button"
                className={`exam-sidebar-tab ${examSidebarMode === 'notes' ? 'active' : ''}`}
                onClick={() => onExamSidebarModeChange('notes')}
                role="tab"
                aria-selected={examSidebarMode === 'notes'}
              >
                <FiFileText size={14} />
                Notes
              </button>
            </div>

            {isLoadingExamTopics ? (
              <div className="exam-sidebar-helper">Loading exam topics...</div>
            ) : examTopicsError ? (
              <div className="exam-sidebar-helper exam-sidebar-helper-error">
                <span>{examTopicsError}</span>
                <button type="button" className="retry-btn" onClick={onRetryExamTopics}>
                  Retry
                </button>
              </div>
            ) : examTopics.length === 0 ? (
              <div className="exam-sidebar-helper">No topics available for this exam.</div>
            ) : (
              <div className="exam-sidebar-topic-list">
                {examSidebarMode === 'topics' &&
                  examNotesTreeNodes.map((node) => {
                    if (node.type === 'topic') {
                      const parentTopic = examTopics.find((topic) => topic.topicId === node.nodeId);
                      const hasSubtopics = Boolean(parentTopic?.subtopics && parentTopic.subtopics.length > 0);

                      if (hasSubtopics) {
                        return (
                          <div key={node.nodeId} className="exam-sidebar-topic-group-title">
                            {node.label}
                          </div>
                        );
                      }

                      const isActive = selectedExamTopicNodeId === node.nodeId;
                      const isLocked = Boolean(lockedExamNodeIds[node.nodeId]);
                      return (
                        <button
                          key={node.nodeId}
                          type="button"
                          className={`exam-sidebar-topic-row ${isActive ? 'active' : ''} ${isLocked ? 'locked' : ''}`}
                          onClick={() => onSelectExamTopic(node.nodeId)}
                          disabled={isLocked}
                          title={isLocked ? 'Locked until active topic is completed.' : undefined}
                        >
                          <span className="exam-sidebar-topic-row-title">{node.label}</span>
                        </button>
                      );
                    }

                    const isActive = selectedExamTopicNodeId === node.nodeId;
                    const isLocked = Boolean(lockedExamNodeIds[node.nodeId]);
                    return (
                      <button
                        key={node.nodeId}
                        type="button"
                        className={`exam-sidebar-topic-row ${isActive ? 'active' : ''} subtopic ${isLocked ? 'locked' : ''}`}
                        onClick={() => onSelectExamTopic(node.nodeId)}
                        disabled={isLocked}
                        title={isLocked ? 'Locked until active subtopic is completed.' : undefined}
                      >
                        <span className="exam-sidebar-topic-row-title">
                          <span className="exam-sidebar-tree-dot" />
                          {node.label}
                        </span>
                      </button>
                    );
                  })}

                {examSidebarMode === 'notes' &&
                  examNotesTreeNodes.map((node) => {
                    if (node.type === 'topic') {
                      const parentTopic = examTopics.find((topic) => topic.topicId === node.nodeId);
                      const hasSubtopics = Boolean(parentTopic?.subtopics && parentTopic.subtopics.length > 0);

                      if (hasSubtopics) {
                        return (
                          <div key={node.nodeId} className="exam-sidebar-topic-group-title">
                            {node.label}
                          </div>
                        );
                      }

                      const isActive = selectedNotesTopicId === node.nodeId;
                      const isLocked = Boolean(lockedExamNodeIds[node.nodeId]);
                      const noteCount = noteCountsByTopic[node.nodeId] || 0;
                      return (
                        <button
                          key={node.nodeId}
                          type="button"
                          className={`exam-sidebar-topic-row ${isActive ? 'active' : ''} ${isLocked ? 'locked' : ''}`}
                          onClick={() => onSelectNotesTopic(node.nodeId)}
                          disabled={isLocked}
                          title={isLocked ? 'Locked until active topic is completed.' : undefined}
                        >
                          <span className="exam-sidebar-topic-row-title">{node.label}</span>
                          {noteCount > 0 && <span className="exam-sidebar-topic-row-count">{noteCount}</span>}
                        </button>
                      );
                    }

                    const isActive = selectedNotesTopicId === node.nodeId;
                    const isLocked = Boolean(lockedExamNodeIds[node.nodeId]);
                    const noteCount = noteCountsByTopic[node.nodeId] || 0;

                    return (
                      <button
                        key={node.nodeId}
                        type="button"
                        className={`exam-sidebar-topic-row ${isActive ? 'active' : ''} subtopic ${isLocked ? 'locked' : ''}`}
                        onClick={() => onSelectNotesTopic(node.nodeId)}
                        disabled={isLocked}
                        title={isLocked ? 'Locked until active subtopic is completed.' : undefined}
                      >
                        <span className="exam-sidebar-topic-row-title">
                          <span className="exam-sidebar-tree-dot" />
                          {node.label}
                        </span>
                        {noteCount > 0 && <span className="exam-sidebar-topic-row-count">{noteCount}</span>}
                      </button>
                    );
                  })}
              </div>
            )}
          </div>
        )}
      </nav>
    </aside>
  );
};

export default ChatSidebar;
